import convert from 'color-convert';
import { darken, lighten, getContrast, parseToHsla } from 'color2k';

const CONTRAST_RATIO = 1.8 // For colours to comply with WCAG 2.0 accessibility, a contrast ratio of 3:1 is required for adjacent colours. However, We are using a contrast of 1.8 to ensure we can create sufficient colours in both directions (lighter and darker)
const CONTRAST_STEP = 0.01 // The amount to lighten/darken a colour

/**
 * Returns true if parameter passed is a valid hex code, returns false otherwise.
 * @param {string} hex - The hex code to test for.
 */
const isHexColourCode = (hex) => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(hex)
}

/**
 * Generates an array of light to dark hex code colours from a given hex code base colour.
 * 
 * Returns undefined if a valid hex code isn't passed as the baseColour.
 * 
 * @param {string} baseColour - The base colour to create the colour palette from.
 */
const generateColourPalette = (baseColour) => {    
    if(!isHexColourCode(baseColour)) return // Return undefined if base colour isn't a hex code

    const palette = [baseColour] // This is the colour palette array we will return

    // Loop through in both directions to generate both light and dark colours
    const directions = ['lighter', 'darker']

    // Set to an arbitrary high number so that we can control the maximum number of attempts to generate colours in both directions. The amount of colours that will be generated depends on the CONTRAST_RATIO
    const maxNumberOfAttempts = 10

    directions.forEach(direction => {
        const referenceColour = direction === 'lighter' ? '#ffffff' : '#000000'
        const adjustFunction = direction === 'lighter' ? lighten : darken

        // Check if colours can be generated from the base colour with enough contrast against the reference colour (white/black), otherwise return
        if(getContrast(baseColour, referenceColour) <= CONTRAST_RATIO ) return

        for (let i = 0; i < maxNumberOfAttempts; i++) {
            // Determine the reference colour for comparison based on the direction.
            // If generating lighter shades, compare against the lightest colour (index 0).
            // If generating darker shades, compare against colours progressively darker than the base color.
            const colourToCompare = direction === 'lighter' ? palette[0] : palette[palette.indexOf(baseColour) + i]
            
            let colourToCalculate = colourToCompare // As a starting point, let colourToCalculate equal the colourToCompare

            let contrast = getContrast(colourToCompare, colourToCalculate) // Get the contrast between the colourToCompare and colourToCalculate
        
            while (contrast < CONTRAST_RATIO) {
                // Keep lightening/darkening the colourToCalculate until the contrast is greater than the CONTRAST_RATIO
                colourToCalculate = adjustFunction(colourToCalculate, CONTRAST_STEP)
                contrast = getContrast(colourToCompare, colourToCalculate)
                
                // If the colourToCalculate reaches the reference colour (either pure white or pure black depending on the direction), we break the loop.
                // Without this logic, we would get stuck in an infinite loop.
                if (getContrast(colourToCalculate, referenceColour) === 1) break
            }
        
            // Continue loop if colour hasn't reached reference colour, otherwise return
            if(getContrast(colourToCalculate, referenceColour) > 1){
                // As the adjustFunctions return colours in hsla, we need the following colourToCalculateHsla and colourToCalculateHex variables to convert the colours back to hex codes
                // Parse hsla colourToCalculate
                const colourToCalculateHsla = parseToHsla(colourToCalculate)

                // Define a hex code from the parsed hsla values. We need to multiply index 1 (saturation) and 2 (lightness) by 100 to get a percentage value
                const colourToCalculateHex = '#' + convert.hsl.hex(colourToCalculateHsla[0], colourToCalculateHsla[1]*100, colourToCalculateHsla[2]*100)

                // In the light direction, add colour to the start of the array.
                // In the dark direction, add colour to the end of the array.
                direction === 'lighter' ? palette.unshift(colourToCalculateHex) : palette.push(colourToCalculateHex)
            } else {
                return
            }
        }
    })

    return palette
} 

export { isHexColourCode, generateColourPalette }
